import {
  UzButton,
  UzCard,
  UzDatePicker,
  UzDialog,
  UzInput,
  UzMoneyInput,
  UzSelect,
  UzTabs,
} from '@utazzo/ui';
import { CheckCircle2, PlusCircle, Receipt } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateDMY, formatInrPaise, StatusBadge } from '../invoices.helpers';
import {
  useBankAccountOptions,
  useInvoiceLedger,
  useRecordReceipt,
  useVoidReceipt,
} from '../use-invoices';

import type {
  InvoiceDetail,
  InvoiceLedgerEvent,
  InvoiceLedgerReceipt,
  InvoiceLedgerResponse,
  RecordReceiptRequest,
} from '@utazzo/shared';

/**
 * Admin-only panel bolted onto the invoice detail page: the payments ledger,
 * the record-payment action and the activity timeline. Rendered OUTSIDE the
 * printable `#invoice-sheet` (inside a `.invoice-no-print` wrapper) so none of
 * it ends up on the printed / PDF'd tax invoice.
 */

/** Static payment modes (mirrors the legacy `createinvoicewithgst` picker). */
const PAYMENT_MODES = ['Bank Transfer', 'Cash', 'Cheque', 'PayU', 'UPI'] as const;

/** Humanized labels for the activity-log event types. */
const EVENT_LABEL: Record<string, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  PAYMENT_RECORDED: 'Payment recorded',
  TAX_INVOICE_ISSUED: 'Tax invoice issued',
  CANCELLED: 'Cancelled',
};

const eventLabel = (type: string): string => EVENT_LABEL[type] ?? type;

/** Date + time in the viewer's locale, e.g. "24-08-2026 15:04". */
const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDateDMY(iso)} ${hh}:${mm}`;
};

/** ISO date (yyyy-mm-dd) in local time, for the receipt-date default + payload. */
const toIsoDate = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Rupees (number) -> integer paise string. */
const rupeesToPaise = (rupees: number): string => String(Math.round(rupees * 100));

/**
 * Diff payloads are `{ before, after }` of a handful of simple scalar fields.
 * Return the keys whose value actually changed, with their before -> after.
 */
type DiffPair = { key: string; before: string; after: string };
const readDiff = (diff: unknown): DiffPair[] => {
  if (!diff || typeof diff !== 'object') return [];
  const d = diff as { before?: Record<string, unknown>; after?: Record<string, unknown> };
  const before = d.before ?? {};
  const after = d.after ?? {};
  if (typeof before !== 'object' || typeof after !== 'object') return [];
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  const pairs: DiffPair[] = [];
  for (const key of keys) {
    const b = before[key];
    const a = after[key];
    if (String(b ?? '') === String(a ?? '')) continue;
    pairs.push({ key, before: String(b ?? '-'), after: String(a ?? '-') });
  }
  return pairs;
};

export const InvoiceAdminPanel = ({ inv }: { inv: InvoiceDetail }): React.JSX.Element => {
  const ledgerQuery = useInvoiceLedger(inv.id);
  const ledger = ledgerQuery.data;

  const isCancelled = inv.status === 'CANCELLED';
  const duePaise = ledger?.summary.amountDuePaise ?? inv.amountDuePaise;
  const paidPaise = ledger?.summary.amountPaidPaise ?? inv.amountPaidPaise;
  const dueIsZero = Number(duePaise) <= 0;

  const receipts = ledger?.receipts ?? [];
  const voidedReceipts = receipts.filter((r) => !!r.voidedAt);
  const events = ledger?.events ?? [];

  return (
    <div className="invoice-no-print mx-auto max-w-3xl space-y-4">
      <StatusStrip inv={inv} paidPaise={paidPaise} duePaise={duePaise} dueIsZero={dueIsZero} />

      <UzTabs
        defaultValue="payments"
        tabs={[
          {
            value: 'payments',
            label: 'Payments',
            content: (
              <PaymentsLedgerCard
                inv={inv}
                ledger={ledger}
                loading={ledgerQuery.isLoading}
                isCancelled={isCancelled}
                duePaise={duePaise}
              />
            ),
          },
          {
            value: 'history',
            label: 'History',
            // Badge counts the edited/voided entries (what "history" means to the
            // user); the activity timeline below it is always present.
            count: voidedReceipts.length || undefined,
            content: (
              <div className="space-y-4">
                <SupersededReceiptsCard receipts={voidedReceipts} loading={ledgerQuery.isLoading} />
                <ActivityLogCard events={events} loading={ledgerQuery.isLoading} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

/* ------------------------------------------------ Superseded (voided) receipts */

const SupersededReceiptsCard = ({
  receipts,
  loading,
}: {
  receipts: InvoiceLedgerReceipt[];
  loading: boolean;
}): React.JSX.Element => (
  <UzCard
    title="Superseded payments"
    description="Receipts that were edited or voided. Kept for audit; excluded from the paid total."
  >
    {loading ? (
      <p className="text-sm text-zinc-500">Loading history...</p>
    ) : receipts.length === 0 ? (
      <p className="text-sm text-zinc-500">No edited or voided receipts.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4 text-right">Amount</th>
              <th className="py-2 pr-4">Mode</th>
              <th className="py-2 pr-4">Bank</th>
              <th className="py-2 pr-4">Recorded by</th>
              <th className="py-2 pr-4">Voided by</th>
              <th className="py-2">Voided at</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 text-zinc-500 last:border-0">
                <td className="py-2 pr-4 whitespace-nowrap">{formatDateDMY(r.receiptDate)}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {formatInrPaise(r.amountPaise)}
                </td>
                <td className="py-2 pr-4">{r.paymentMode ?? '-'}</td>
                <td className="py-2 pr-4">{r.bankName ?? '-'}</td>
                <td className="py-2 pr-4">{r.createdByName ?? '-'}</td>
                <td className="py-2 pr-4">{r.voidedByName ?? '-'}</td>
                <td className="py-2 whitespace-nowrap">
                  {r.voidedAt ? formatDateTime(r.voidedAt) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </UzCard>
);

/* ---------------------------------------------------------------- Status strip */

const StatusStrip = ({
  inv,
  paidPaise,
  duePaise,
  dueIsZero,
}: {
  inv: InvoiceDetail;
  paidPaise: string;
  duePaise: string;
  dueIsZero: boolean;
}): React.JSX.Element => (
  <UzCard>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Proforma No.</span>
          <span className="text-sm font-semibold text-zinc-900">{inv.proformaNumber ?? '-'}</span>
          <StatusBadge status={inv.status} />
        </div>
        {inv.taxInvoiceNumber && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-zinc-500">Tax Invoice No.</span>
            <span className="text-sm font-semibold text-zinc-900">{inv.taxInvoiceNumber}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <Metric label="Grand total" value={formatInrPaise(inv.grandTotalPaise)} />
        <Metric label="Paid" value={formatInrPaise(paidPaise)} />
        {dueIsZero ? (
          <div className="text-right">
            <div className="text-xs font-medium text-zinc-500">Due</div>
            <div className="inline-flex items-center gap-1 text-sm font-semibold text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              Paid in full
            </div>
          </div>
        ) : (
          <Metric label="Due" value={formatInrPaise(duePaise)} highlight />
        )}
        {Number(inv.totalItcPaise) > 0 && (
          <div className="text-right">
            <div className="text-xs font-medium text-zinc-500">Input tax credit (ITC)</div>
            <div className="text-sm font-semibold text-emerald-600">
              {formatInrPaise(inv.totalItcPaise)}
            </div>
            <div className="text-[10px] text-zinc-400">Reclaimable supplier GST (internal)</div>
          </div>
        )}
      </div>
    </div>
  </UzCard>
);

const Metric = ({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): React.JSX.Element => (
  <div className="text-right">
    <div className="text-xs font-medium text-zinc-500">{label}</div>
    <div
      className={
        highlight ? 'text-sm font-semibold text-red-600' : 'text-sm font-semibold text-zinc-900'
      }
    >
      {value}
    </div>
  </div>
);

/* -------------------------------------------------------------- Payments ledger */

const PaymentsLedgerCard = ({
  inv,
  ledger,
  loading,
  isCancelled,
  duePaise,
}: {
  inv: InvoiceDetail;
  ledger: InvoiceLedgerResponse | undefined;
  loading: boolean;
  isCancelled: boolean;
  duePaise: string;
}): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  // The receipt being edited (void + re-record), or null for a fresh entry.
  const [editing, setEditing] = useState<InvoiceLedgerReceipt | null>(null);
  const voidReceipt = useVoidReceipt(inv.id);
  const receipts = ledger?.receipts ?? [];

  const openRecord = (): void => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (r: InvoiceLedgerReceipt): void => {
    setEditing(r);
    setDialogOpen(true);
  };

  // The ledger shows only the current (non-voided) receipts; superseded ones
  // live in the History tab.
  const currentReceipts = receipts.filter((r) => !r.voidedAt);
  const receiptHref = `/invoices/${inv.id}/receipt/print`;

  return (
    <UzCard
      title="Payments ledger"
      actions={
        <div className="flex items-center gap-2">
          {currentReceipts.length > 0 && (
            <UzButton asChild size="sm" variant="outline">
              <Link to={receiptHref} target="_blank" rel="noopener noreferrer">
                <Receipt className="mr-1.5 h-4 w-4" />
                Print receipt
              </Link>
            </UzButton>
          )}
          {!isCancelled && (
            <UzButton size="sm" leftIcon={<PlusCircle className="h-4 w-4" />} onClick={openRecord}>
              Record payment
            </UzButton>
          )}
        </div>
      }
    >
      {loading ? (
        <p className="text-sm text-zinc-500">Loading payments...</p>
      ) : currentReceipts.length === 0 ? (
        <p className="text-sm text-zinc-500">No payments recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4 text-right">Amount</th>
                <th className="py-2 pr-4">Mode</th>
                <th className="py-2 pr-4">Reference</th>
                <th className="py-2 pr-4">Bank</th>
                <th className="py-2 pr-4">Notes</th>
                <th className="py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {currentReceipts.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 pr-4 whitespace-nowrap">{formatDateDMY(r.receiptDate)}</td>
                  <td className="py-2 pr-4 text-right whitespace-nowrap tabular-nums">
                    {formatInrPaise(r.amountPaise)}
                  </td>
                  <td className="py-2 pr-4">{r.paymentMode ?? '-'}</td>
                  <td className="py-2 pr-4">{r.referenceNo ?? '-'}</td>
                  <td className="py-2 pr-4">{r.bankName ?? '-'}</td>
                  <td className="py-2 pr-4">{r.notes ?? '-'}</td>
                  <td className="py-2 text-right">
                    <div className="flex justify-end gap-3">
                      <Link
                        to={`${receiptHref}?r=${r.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-zinc-600 hover:underline"
                      >
                        Receipt
                      </Link>
                      {!isCancelled && (
                        <>
                          <button
                            type="button"
                            className="text-xs font-medium text-zinc-600 hover:underline"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                            disabled={voidReceipt.isPending}
                            onClick={() => {
                              if (
                                window.confirm('Void this payment? It will be excluded from paid.')
                              )
                                voidReceipt.mutate(r.id);
                            }}
                          >
                            Void
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isCancelled && (
        <RecordPaymentDialog
          invoiceId={inv.id}
          open={dialogOpen}
          onOpenChange={(o) => {
            setDialogOpen(o);
            if (!o) setEditing(null);
          }}
          duePaise={duePaise}
          editing={editing}
        />
      )}
    </UzCard>
  );
};

/* ------------------------------------------------------------ Record-payment UI */

const RecordPaymentDialog = ({
  invoiceId,
  open,
  onOpenChange,
  duePaise,
  editing,
}: {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duePaise: string;
  editing: InvoiceLedgerReceipt | null;
}): React.JSX.Element => {
  const recordReceipt = useRecordReceipt(invoiceId);
  const voidReceipt = useVoidReceipt(invoiceId);
  const bankOptions = useBankAccountOptions();
  const isEdit = !!editing;

  // When editing, the old receipt is voided and re-recorded, so the amount may
  // occupy its own value plus whatever is currently due.
  const dueRupees = useMemo(() => {
    const base = Number(duePaise) + (editing ? Number(editing.amountPaise) : 0);
    return base / 100;
  }, [duePaise, editing]);
  const [amount, setAmount] = useState<number | ''>(dueRupees > 0 ? dueRupees : '');
  const [receiptDate, setReceiptDate] = useState<Date | null>(new Date());
  const [paymentMode, setPaymentMode] = useState<string>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [notes, setNotes] = useState('');

  // Reset/prefill the fields each time the dialog re-opens.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setAmount(Number(editing.amountPaise) / 100);
      setReceiptDate(editing.receiptDate ? new Date(editing.receiptDate) : new Date());
      setPaymentMode(editing.paymentMode ?? '');
      setReferenceNo(editing.referenceNo ?? '');
      setBankAccountId(editing.bankAccountId ?? '');
      setNotes(editing.notes ?? '');
    } else {
      setAmount(dueRupees > 0 ? dueRupees : '');
      setReceiptDate(new Date());
      setPaymentMode('');
      setReferenceNo('');
      setBankAccountId('');
      setNotes('');
    }
  }, [open, dueRupees, editing]);

  const busy = recordReceipt.isPending || voidReceipt.isPending;
  const amountValid = typeof amount === 'number' && amount > 0;
  // Cannot collect more than is due (the server rejects this too).
  const overDue = typeof amount === 'number' && dueRupees > 0 && amount > dueRupees;
  const canSubmit = amountValid && !overDue && !!receiptDate && !!bankAccountId && !busy;

  const handleSubmit = (): void => {
    if (!amountValid || !receiptDate || !bankAccountId) return;
    const body: RecordReceiptRequest = {
      amountPaise: rupeesToPaise(amount),
      receiptDate: toIsoDate(receiptDate),
      bankAccountId,
      ...(paymentMode ? { paymentMode } : {}),
      ...(referenceNo.trim() ? { referenceNo: referenceNo.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    // Edit = void the old receipt, then record the corrected one.
    const doRecord = (): void => {
      recordReceipt.mutate(body, { onSuccess: () => onOpenChange(false) });
    };
    if (editing) {
      voidReceipt.mutate(editing.id, { onSuccess: doRecord });
    } else {
      doRecord();
    }
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit payment' : 'Record payment'}
      description={
        isEdit
          ? 'Void the original receipt and re-record it with the corrected details.'
          : 'Log a payment received against this invoice.'
      }
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </UzButton>
          <UzButton onClick={handleSubmit} disabled={!canSubmit} loading={busy}>
            {isEdit ? 'Save changes' : 'Record payment'}
          </UzButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <UzMoneyInput
          label="Amount"
          required
          value={amount}
          onValueChange={(v) => setAmount(v ?? '')}
          error={overDue ? `Cannot exceed the due of ${formatInrPaise(duePaise)}` : undefined}
        />
        <UzDatePicker
          label="Receipt date"
          required
          value={receiptDate}
          onValueChange={setReceiptDate}
        />
        <UzSelect
          label="Payment mode"
          placeholder="Select mode"
          value={paymentMode}
          onValueChange={setPaymentMode}
          options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))}
        />
        <UzInput
          label="Reference no"
          value={referenceNo}
          onChange={(e) => setReferenceNo(e.target.value)}
        />
        <UzSelect
          label="Received in bank"
          required
          placeholder="Select bank account"
          value={bankAccountId}
          onValueChange={setBankAccountId}
          options={(bankOptions.data ?? []).map((b) => ({
            value: b.id,
            label: `${b.label} - ${b.bankName} (${b.accountNumber})`,
          }))}
        />
        <UzInput
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          containerClassName="sm:col-span-2"
        />
      </div>
    </UzDialog>
  );
};

/* ----------------------------------------------------------------- Activity log */

const ActivityLogCard = ({
  events,
  loading,
}: {
  events: InvoiceLedgerEvent[];
  loading: boolean;
}): React.JSX.Element => (
  <UzCard title="Activity log">
    {loading ? (
      <p className="text-sm text-zinc-500">Loading activity...</p>
    ) : events.length === 0 ? (
      <p className="text-sm text-zinc-500">No activity yet.</p>
    ) : (
      <ol className="space-y-4">
        {events.map((e) => (
          <TimelineItem key={e.id} event={e} />
        ))}
      </ol>
    )}
  </UzCard>
);

const TimelineItem = ({ event }: { event: InvoiceLedgerEvent }): React.JSX.Element => {
  const diffPairs = readDiff(event.diff);
  return (
    <li className="flex gap-3">
      <div className="mt-1.5 flex flex-col items-center">
        <span className="h-2 w-2 shrink-0 rounded-full bg-zinc-400" />
        <span className="mt-1 w-px flex-1 bg-zinc-200" />
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-semibold text-zinc-900">{eventLabel(event.type)}</span>
          {event.actorName && <span className="text-xs text-zinc-500">by {event.actorName}</span>}
          <span className="text-xs text-zinc-400">{formatDateTime(event.createdAt)}</span>
        </div>
        {event.summary && <div className="text-sm text-zinc-600">{event.summary}</div>}
        {diffPairs.length > 0 && (
          <div className="mt-1 text-xs text-zinc-500">
            changed:{' '}
            {diffPairs.map((p, i) => (
              <span key={p.key}>
                {i > 0 && ', '}
                <span className="font-medium">{p.key}</span> ({p.before} {'->'} {p.after})
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};
